// ─────────────────────────────────────────────
//  useCaseCommit — what happens when a dragged case bar is let go.
//
//  Validate → write → offer an undo → and only then tell the room.
//
//  ⚠️ THE UNDO WINDOW COMES BEFORE THE NOTIFICATION, and that ordering is the
//  whole point of this file. A drag is a fast gesture repeated dozens of times a
//  day; committing AND paging on every release means a board runner tidying ten
//  blocks buzzes twenty phones, and an accidental 15-minute nudge wakes someone
//  mid-case. So the board is corrected IMMEDIATELY (it must be right, and every
//  open board is watching), the operator gets an Undo, and the `createTask` is
//  held until that window closes. Undo restores the previous times AND cancels
//  the message, so a change that did not happen never pages anyone.
//
//  A confirm dialog was the alternative and is worse: a confirmation on a
//  gesture performed fifty times a day is clicked through blind.
//
//  ⚠️ NOTHING HERE INVENTS A SECOND NOTIFICATION VOCABULARY. The recipients are
//  resolved with `uidOf`, the document is built by `buildManualTask` and the
//  sentence comes from `assignmentMessage` — the same three calls
//  `AssignmentModal.js:318-363` makes. The server trigger will NOT do this for
//  us: `sendAssignmentTasks` keys off identity fields only, and `startTime` /
//  `endTime` appear nowhere in it.
// ─────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { updateAssignmentRow, createTask } from "../../firebase/firestore";
import { commitDecision } from "../../domain/timelineCommit";
import { assignmentMessage } from "../../domain/assignmentNotice";
import { buildManualTask } from "../../domain/tasks";
import { uidOf } from "../../domain/channels";
import { to12h, hmToMinutes } from "../../domain/timeline";

// How long the operator has to take it back. Long enough to notice a mistake on
// a wall board across the room, short enough that the people in the room are not
// left waiting on news that has already changed the schedule.
export const UNDO_WINDOW_MS = 8000;

const span12h = (startTime, endTime) =>
  [startTime, endTime].map((t) => to12h(hmToMinutes(t))).filter(Boolean).join("–");

export default function useCaseCommit({
  sections = [],
  placed = [],
  facilityId,
  // ⚠️ THE PAGE'S DATE, NOT `rosterDateKey()`. `AssignmentModal` hardcodes today
  // because the OR Board IS today; this page has a free date picker, and the
  // task lane is day-scoped. Stamping a directive about Thursday's board with
  // today's key would put it on phones as if it were now.
  dateKey,
  realUser,
  // The pools a staff id is resolved to an auth uid through.
  staff = [],
  doctors = [],
  providers = [],
  toast,
  // () => boolean. The page's live write gate (role · connection band · preview).
  guard,
}) {
  const [pending, setPending] = useState(null);
  const [announcement, setAnnouncement] = useState("");

  // The authoritative copy. State is only a mirror for rendering: the timer
  // callback must see the record even if a re-render has not landed.
  const pendingRef = useRef(null);
  const timerRef = useRef(null);

  const roomCasesById = useMemo(() => {
    const m = new Map();
    for (const sec of sections || []) {
      for (const room of sec.rooms || []) m.set(room.id, room.cases || []);
    }
    return m;
  }, [sections]);

  const roomNames = useMemo(() => {
    const m = {};
    for (const sec of sections || []) {
      for (const room of sec.rooms || []) m[room.id] = room.name;
    }
    return m;
  }, [sections]);

  const pool = useMemo(
    () => [...(staff || []), ...(doctors || []), ...(providers || [])],
    [staff, doctors, providers]
  );
  const poolRef = useRef(pool);
  poolRef.current = pool;
  const senderRef = useRef({ facilityId, dateKey, realUser });
  senderRef.current = { facilityId, dateKey, realUser };
  // Held in a ref so `commit` keeps ONE identity. The page passes an inline
  // arrow (it closes over the live connection band), and listing it as a
  // dependency would mint a new `commit` on every render — which then re-renders
  // every bar on the chart, mid-drag included.
  const guardRef = useRef(guard);
  guardRef.current = guard;

  // ── The message, sent only once the undo window has closed ────────────────
  const sendNotice = useCallback((record) => {
    if (!record) return Promise.resolve(0);
    const { facilityId: fid, dateKey: dk, realUser: me } = senderRef.current;
    const c = record.caseRow;
    // ⚠️ AUTH UIDS, NOT STAFF IDS. The two are indistinguishable strings: a
    // staff id writes cleanly, shows as sent, and rings no phone.
    const onRow = [
      { id: c.ahpId, name: c.ahpName || c.provider || "" },
      { id: c.attendingId, name: c.attendingName || c.attending || "" },
    ]
      .filter((p) => p.id)
      .map((p) => ({ ...p, uid: uidOf(poolRef.current, p.id) }));
    const recipientIds = Array.from(new Set(onRow.filter((p) => p.uid).map((p) => p.uid)));
    const payload = buildManualTask({
      facilityId: fid || "default",
      dateKey: dk,
      senderId: me?.uid,
      senderName: me?.name || "",
      recipientIds,
      text: assignmentMessage({
        roomName: record.roomName,
        startTime: record.next.startTime,
        endTime: record.next.endTime,
      }),
      roomName: record.roomName || null,
    });
    // A null payload means nobody reachable (or no sender) — nothing to send,
    // and not a failure to report as one.
    if (!payload) return Promise.resolve(0);
    return createTask(payload)
      .then(() => payload.recipientIds.length)
      .catch((err) => {
        // ⚠️ NEVER UNDOES THE SAVE. The assignment is committed and is already
        // on every open board; rolling a clinical write back because a message
        // failed would be far the worse outcome.
        console.error("[Timeline] time-change directive failed:", err);
        return -1;
      });
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Close the window NOW and send. Used by the timer, and by a second drag on a
  // DIFFERENT case — that first change survived, so its people are told.
  const flush = useCallback(
    (record) => {
      if (!record) return;
      sendNotice(record).then((n) => {
        if (!toast) return;
        if (n < 0) toast.error(`${record.roomName} was updated, but the message could not be sent.`);
        else if (n > 0) toast.info(`${record.roomName} — message sent to ${n}.`);
      });
    },
    [sendNotice, toast]
  );

  // ⚠️ CLEAR THE TIMER ON UNMOUNT. A `setTimeout` that fires after the page has
  // navigated away sets state on a dead tree. The notice is still SENT — by then
  // nobody can undo it, so the change definitively survived — but it is sent
  // directly, touching no state.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      const record = pendingRef.current;
      pendingRef.current = null;
      if (record) {
        sendNotice(record).catch(() => {});
      }
    },
    [sendNotice]
  );

  const commit = useCallback(
    async ({ caseRow, startTime, endTime }) => {
      if (!caseRow || !caseRow.id) return false;
      if (guardRef.current && !guardRef.current()) return false;

      const roomCases = roomCasesById.get(caseRow.roomId) || [caseRow];
      const decision = commitDecision({
        caseRow,
        roomCases,
        nextStartTime: startTime,
        nextEndTime: endTime,
        placed,
        roomNames,
      });

      if (!decision.ok) {
        // ⚠️ VERBATIM. `validateCaseTimes` already names the colliding case's
        // real times, and the edit modal says exactly this sentence about the
        // identical situation. Re-wording it here is how the two surfaces start
        // disagreeing about the same board.
        //
        // ⚠️ AND THIS DOES NOT CONTRADICT THE SPLIT-CASE FEATURE.
        // `doubleBooking.js:70` treats a same-room overlap as legitimate and
        // `AssignmentModal`'s new-case branch deliberately makes one — both are
        // about ADDING a case on purpose. Sliding an existing case on top of
        // another, with a mouse, where the two then draw stacked and read as one
        // long case, is the opposite gesture. Do not "fix" this to match them.
        if (toast) toast.error(decision.error);
        return false;
      }

      const prev = { startTime: caseRow.startTime, endTime: caseRow.endTime };
      try {
        await updateAssignmentRow(caseRow.id, decision.patch);
      } catch (err) {
        console.error("[Timeline] case time write failed:", err);
        if (toast) toast.error("That time change could not be saved. Please try again.");
        return false;
      }

      const roomName = roomNames[caseRow.roomId] || caseRow.roomName || "That room";
      const prior = pendingRef.current;
      clearTimer();
      // The SAME bar nudged twice inside one window: keep the original `prev` so
      // Undo returns to where the operator started, and restart the clock. A
      // DIFFERENT bar means the first change has been left alone — close its
      // window and tell its room.
      if (prior && prior.caseRow.id !== caseRow.id) flush(prior);

      const record = {
        caseRow,
        roomName,
        prev: prior && prior.caseRow.id === caseRow.id ? prior.prev : prev,
        next: decision.patch,
        warning: decision.warning,
      };
      pendingRef.current = record;
      setPending(record);
      setAnnouncement(
        [
          `${roomName} now ${span12h(decision.patch.startTime, decision.patch.endTime)}.`,
          decision.warning || "",
          "Undo is available for a few seconds.",
        ]
          .filter(Boolean)
          .join(" ")
      );
      if (decision.warning && toast) toast.warning(decision.warning);

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const rec = pendingRef.current;
        pendingRef.current = null;
        setPending(null);
        flush(rec);
      }, UNDO_WINDOW_MS);

      return true;
    },
    [roomCasesById, roomNames, placed, toast, clearTimer, flush]
  );

  const undo = useCallback(async () => {
    const record = pendingRef.current;
    if (!record) return;
    clearTimer();
    pendingRef.current = null;
    setPending(null);
    try {
      await updateAssignmentRow(record.caseRow.id, record.prev);
      setAnnouncement(
        `${record.roomName} put back to ${span12h(record.prev.startTime, record.prev.endTime)}. Nobody was messaged.`
      );
    } catch (err) {
      console.error("[Timeline] undo failed:", err);
      if (toast) toast.error("That change could not be undone. Edit the times on the case instead.");
    }
  }, [clearTimer, toast]);

  return { commit, undo, pending, announcement };
}
